import { ExpenseClaimLine } from './expenseClaims'
import {
  InvoiceAutomaticDebtCollectionStatus,
  InvoiceDeliveryMethod,
  InvoiceRecurrenceMode,
  InvoiceRecurrencePreset,
  InvoiceSoftReminderStatus,
  InvoiceStatus,
  PurchaseInvoiceImportSource,
  ReferenceMethods,
  VatCode
} from '@constants'
import { network, saveBlobAsFile } from '@utils'
import { IContact } from './contacts'
import { IFile } from './files'
import { handleReturn, MutationFunction, PaginatedResponse, QueryFunction } from './root'
import { IPaymentData, IPISPayment } from '@query/payments.ts'
import { IDocument, IImportData } from './documents'

/* ======
 PRODUCTS
======= */

export interface IProduct {
  id: number
  code: string
  name: string
  unit: string
  amount: number
  vat_rate: number
  vat_code: VatCode
  is_vat_inclusive: boolean
  vat_exclusive_amount: number
  vat_inclusive_amount: number
  vat_amount: number
  account_id: number
  description?: string | null
}

interface ProductListProps {
  slug: string
}

export const fetchProducts: QueryFunction<ProductListProps, PaginatedResponse<IProduct>> = async (
  { slug },
  params = null
) => {
  const url = `/v1/invoicing/${slug}/product/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params
  })
  return data
}

export const exportProductsCsv = async (
  { slug }: ProductListProps,
  params: Record<string, unknown> | null = null
): Promise<void> => {
  const url = `/v1/invoicing/${slug}/product/export/csv/`
  const response = await network.httpClient.request({
    url,
    method: 'GET',
    params,
    responseType: 'blob'
  })

  const filename =
    response.headers?.['content-disposition']?.split('filename=')[1]?.slice(1, -1) ??
    `${slug}_products.csv`

  saveBlobAsFile(response.data, filename)
}

export const createProduct: MutationFunction<ProductListProps, IProduct> = async (
  { slug },
  requestData
) => {
  const url = `/v1/invoicing/${slug}/product/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'POST',
    data: requestData,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

interface ProductInstanceProps extends ProductListProps {
  productId: number
}

export const fetchProduct: QueryFunction<ProductInstanceProps, IProduct> = async (
  { slug, productId },
  params = null
) => {
  const url = `/v1/invoicing/${slug}/product/${productId}/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params
  })
  return data
}

export const updateProduct: QueryFunction<ProductInstanceProps, IProduct> = async (
  { slug, productId },
  requestData
) => {
  const url = `/v1/invoicing/${slug}/product/${productId}/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'PATCH',
    data: requestData,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const deleteProduct: MutationFunction<ProductInstanceProps, void> = async (
  { slug, productId },
  params = null
) => {
  const url = `/v1/invoicing/${slug}/product/${productId}/`
  await network.httpClient.request({
    url,
    method: 'DELETE',
    params
  })
}

/* ======
 INVOICES
======= */

export interface IRecurenceRule {
  start: Date
  rule: {
    frequency: number
    interval?: number
    by_month_day: Array<number>
    by_month: Array<number>
  }
}

export interface IInvoiceRecurence extends IRecurenceRule {
  recurrence_rule: IRecurenceRule
  is_active_recurrence_template: boolean
  is_recurrence_end_exceeded: boolean
  recurrence_parent_id?: number
  recurrence_reference_method: ReferenceMethods
  recurrence_email_subject?: string
  recurrence_email_content?: string
  next_recurrences?: Array<Date>
  recurrence_end: Date | string
  is_recurrence_active: boolean
  is_recurrence_applied: boolean
  delivery_method: InvoiceDeliveryMethod
}

export interface IInvoiceRow {
  id: number
  product?: number
  code: string
  name: string
  unit: string
  amount: number
  vat_code: VatCode
  vat_rate: number
  product_count: number
  description: string
  total_amount: number
  total_vat_amount: number
  account_id: number
}

export type PostDueReminderMark = 'sent' | 'failed'

export type PostDueScheduleKind = 'invoice_sent' | 'reminder' | 'collection'

export type PostDueScheduleState =
  | 'sent'
  | 'failed'
  | 'scheduled'
  | 'waiting'
  | 'ready'
  | 'automatic'

export interface IPostDueScheduleItem {
  kind: PostDueScheduleKind
  state: PostDueScheduleState
  at: string
  reminder_number?: number
  delivery_method?: string
}

export interface IPostDue {
  reminder_mark: PostDueReminderMark | null
  kravia_registered: boolean
  schedule: IPostDueScheduleItem[]
}

export interface IInvoice extends IInvoiceRecurence {
  id: number
  friendly_name: string
  receiver: number
  receiver_info?: IContact
  contact_person?: string
  invoice_number: string
  invoicing_date: string
  payment_condition_days: number
  due_date: string
  settlement_date: string
  reference: string
  penalty_interest: number
  currency: string
  vat_country: string
  description: string
  is_credit_note_for: number
  credit_notes: number[]
  rows: IInvoiceRow[]
  pdf?: IFile
  xrechnung_ubl_xml?: string | null
  total_amount: number
  status: InvoiceStatus
  total_vat_amount: number
  is_editable?: boolean
  is_deletable?: boolean
  is_past_due: boolean
  is_registered_to_kravia: boolean
  earliest_debt_collection_date: string
  delivery_method: InvoiceDeliveryMethod
  last_delivery_at: string
  attachments: number[]
  tag_ids: number[]
  is_sendable: boolean
  seller_reference: string
  buyer_reference: string
  payment_date?: string
  bank_transaction?: IImportData
  document?: IDocument
  is_automatic_debt_collection_enabled: boolean
  automatic_debt_collection_status: InvoiceAutomaticDebtCollectionStatus
  is_automatic_soft_reminder_enabled: boolean
  automatic_soft_reminder_status: InvoiceSoftReminderStatus
  soft_reminder_sent_count: number
  post_due?: IPostDue
}

export interface IInvoiceFormValues extends Partial<IInvoice> {
  recurrence_mode?: InvoiceRecurrenceMode
  recurrence_interval_preset?: InvoiceRecurrencePreset
}

export interface IPurchaseInvoice {
  id: number
  invoice_number: string
  invoicing_date: string
  delivery_date?: string | null
  due_date: string
  sender_vat: string
  sender_name: string
  sender_bank_account: string
  sender_bank_bic?: string
  receiver_vat: string
  receiver_name: string
  reference?: string
  message?: string
  original_invoice_xml: string
  original_attachment: IFile
  amount: number
  currency: string
  virtuaaliviivakoodi?: string
  is_paid: boolean
  is_past_due: boolean
  is_deletable?: boolean
  attachments: number[]
  tag_ids: number[]
  import_source: PurchaseInvoiceImportSource
  payment?: IPaymentData
  pis_payment?: IPISPayment
  bank_transaction?: IImportData
  document?: IDocument
  payment_date?: string
  expense_claim?: {
    id: number
    number: string
    approved_at: string | null
    approved_as_accrual_based: boolean | null
    booking_date: string | null
    document_id: number | null
    counter_account: {
      id: number
      number: string
      name: string
    } | null
    approver_name: string | null
    approver: {
      id: number
      first_name: string
      last_name: string
      name: string | null
    } | null
    lines: ExpenseClaimLine[]
    attachment_items: {
      id: number
      kind: 'statement' | 'receipt'
      merchant?: string
      amount?: number | null
    }[]
  } | null
}

interface InvoiceListProps {
  slug: string
}

export const fetchInvoices: QueryFunction<InvoiceListProps, PaginatedResponse<IInvoice>> = async (
  { slug },
  params
) => {
  const url = `/v1/invoicing/${slug}/invoice/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params: params
  })
  return data
}

interface IPurchaseInvoiceItem {
  type: 'purchase'
  item: IPurchaseInvoice
}

interface InvoiceItem {
  type: 'sales'
  item: IInvoice
}

export type ICombinedInvoice = InvoiceItem | IPurchaseInvoiceItem

export const fetchCombinedInvoices: QueryFunction<
  InvoiceListProps,
  PaginatedResponse<ICombinedInvoice>
> = async ({ slug }, params) => {
  const url = `/v1/invoicing/${slug}/invoice_combined/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params
  })
  return data
}

export const createInvoice: MutationFunction<InvoiceListProps, IInvoice> = async (
  { slug },
  requestData
) => {
  const url = `/v1/invoicing/${slug}/invoice/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'POST',
    data: requestData,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

interface InvoiceInstanceProps extends InvoiceListProps {
  slug: string
  invoiceId: number
}

export const fetchInvoice: QueryFunction<InvoiceInstanceProps, IInvoice> = async (
  { slug, invoiceId },
  params = null
) => {
  const url = `/v1/invoicing/${slug}/invoice/${invoiceId}/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params
  })
  return data
}

export const updateInvoice: MutationFunction<InvoiceInstanceProps, IInvoice> = async (
  { slug, invoiceId },
  requestData
) => {
  const url = `/v1/invoicing/${slug}/invoice/${invoiceId}/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'PATCH',
    data: requestData,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const deleteInvoice: MutationFunction<InvoiceInstanceProps, void> = async (
  { slug, invoiceId },
  params = null
) => {
  const url = `/v1/invoicing/${slug}/invoice/${invoiceId}/`
  await network.httpClient.request({
    url,
    method: 'DELETE',
    params
  })
}

interface PurchaseInvoiceInstanceProps extends InvoiceListProps {
  slug: string
  purchaseInvoiceId: number
}

export const fetchPurchaseInvoice: QueryFunction<
  PurchaseInvoiceInstanceProps,
  IPurchaseInvoice
> = async ({ slug, purchaseInvoiceId }, params = null) => {
  const url = `/v1/invoicing/${slug}/purchase_invoice/${purchaseInvoiceId}/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params
  })
  return data
}

export const updatePurchaseInvoice: MutationFunction<
  PurchaseInvoiceInstanceProps,
  IPurchaseInvoice
> = async ({ slug, purchaseInvoiceId }, requestData) => {
  const url = `/v1/invoicing/${slug}/purchase_invoice/${purchaseInvoiceId}/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'PATCH',
    data: requestData,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const deletePurchaseInvoice: MutationFunction<PurchaseInvoiceInstanceProps, void> = async (
  { slug, purchaseInvoiceId },
  params = null
) => {
  const url = `/v1/invoicing/${slug}/purchase_invoice/${purchaseInvoiceId}/`
  await network.httpClient.request({
    url,
    method: 'DELETE',
    params
  })
}

export interface SendInvoiceViaEmailProps {
  email_subject?: string
  email_content?: string
}

export interface SendInvoiceProps {
  delivery_method: InvoiceDeliveryMethod
  data?: SendInvoiceViaEmailProps
}

export interface InvoiceDeliveryMethodInfo {
  name: string
  info?: string
  method: InvoiceDeliveryMethod
  errors: Array<string>
}

export const fetchInvoiceDeliveryMethods: QueryFunction<
  InvoiceInstanceProps,
  Array<InvoiceDeliveryMethodInfo>
> = async ({ slug, invoiceId }) => {
  const url = `/v1/invoicing/${slug}/invoice/${invoiceId}/delivery_methods/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export const sendInvoice: MutationFunction<InvoiceInstanceProps, SendInvoiceProps> = async (
  { slug, invoiceId },
  requestData
) => {
  const url = `/v1/invoicing/${slug}/invoice/${invoiceId}/send/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'POST',
    data: requestData,
    validateStatus: () => true,
    timeout: 120000 // 2 min
  })
  return handleReturn({ status, data })
}

export interface UpdateInvoicePostDueAutomationProps {
  is_automatic_soft_reminder_enabled?: boolean
  is_automatic_debt_collection_enabled?: boolean
}

export const updateInvoicePostDueAutomation: MutationFunction<
  InvoiceInstanceProps,
  UpdateInvoicePostDueAutomationProps
> = async ({ slug, invoiceId }, requestData) => {
  const url = `/v1/invoicing/${slug}/invoice/${invoiceId}/actions/post_due_automation/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'POST',
    data: requestData,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const acceptInvoice: MutationFunction<InvoiceInstanceProps, null> = async ({
  slug,
  invoiceId
}) => {
  const url = `/v1/invoicing/${slug}/invoice/${invoiceId}/actions/accept/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'POST',
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const setInvoiceAsPaid: MutationFunction<InvoiceInstanceProps, null> = async ({
  slug,
  invoiceId
}) => {
  const url = `/v1/invoicing/${slug}/invoice/${invoiceId}/actions/paid/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'POST',
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const setInvoiceAsUnpaid: MutationFunction<InvoiceInstanceProps, null> = async ({
  slug,
  invoiceId
}) => {
  const url = `/v1/invoicing/${slug}/invoice/${invoiceId}/actions/unpaid/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'POST',
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const setInvoiceAsCreditLoss: MutationFunction<InvoiceInstanceProps, null> = async ({
  slug,
  invoiceId
}) => {
  const url = `/v1/invoicing/${slug}/invoice/${invoiceId}/actions/credit_loss/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'POST',
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

interface FetchInvoiceNumberProps {
  slug: string
}

export interface IInvoiceNumber {
  next_invoice_number: IInvoice['invoice_number']
}

export const fetchInvoiceNumber: QueryFunction<FetchInvoiceNumberProps, IInvoiceNumber> = async ({
  slug
}) => {
  const url = `/v1/invoicing/${slug}/invoice_number/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

interface UpdateInvoiceNumberProps {
  slug: string
}

export const updateInvoiceNumber: MutationFunction<
  UpdateInvoiceNumberProps,
  IInvoiceNumber
> = async ({ slug }, requestData) => {
  const url = `/v1/invoicing/${slug}/invoice_number/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'PATCH',
    data: requestData,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export interface DisableInvoiceRecurrenceProps {
  slug: string
  invoiceId: number
}

export const disableInvoiceRecurrence: MutationFunction<
  DisableInvoiceRecurrenceProps,
  undefined
> = async ({ slug, invoiceId }) => {
  const url = `/v1/invoicing/${slug}/invoice/${invoiceId}/actions/disable_recurrence/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'POST',
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export interface IEmailReceiveAddress {
  email: string
}

interface EmailReceiveAddressProps {
  slug: string
}

export const fetchEmailInvoiceAddress: QueryFunction<
  EmailReceiveAddressProps,
  IEmailReceiveAddress
> = async ({ slug }) => {
  const url = `/v1/invoicing/${slug}/email_receive_address/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export type PurchaseInvoiceImportStatus = 'upload_started' | 'analyzing' | 'completed' | 'failed'

export interface IPurchaseInvoiceImportStartResponse {
  job_id: string
  attachment_id: number
  purchase_invoice_id: number
  status: Extract<PurchaseInvoiceImportStatus, 'upload_started' | 'completed'>
}

export interface IPurchaseInvoiceImportStatusResponse {
  job_id: string
  attachment_id: number
  purchase_invoice_id: number | null
  status: PurchaseInvoiceImportStatus
  error_code?: string
  error_message?: string
}

export interface IPurchaseInvoiceImportErrorPayload {
  error_code?: string
  error_message?: string
  attachment_id?: number
  purchase_invoice_id?: number
}

interface StartPurchaseInvoiceImportRequest {
  file: File
  name?: string
}

export const startPurchaseInvoiceImport = async (
  { slug }: InvoiceListProps,
  { file, name }: StartPurchaseInvoiceImportRequest
): Promise<IPurchaseInvoiceImportStartResponse> => {
  const url = `/v1/invoicing/${slug}/purchase_invoice/import/`
  const formData = new FormData()
  formData.append('file', file)

  if (name) {
    formData.append('name', name)
  }

  const { status, data } = await network.httpClient.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    validateStatus: () => true,
    timeout: 60000
  })

  return handleReturn({ status, data })
}

interface PurchaseInvoiceImportStatusProps extends InvoiceListProps {
  jobId: string
}

export const fetchPurchaseInvoiceImportStatus: QueryFunction<
  PurchaseInvoiceImportStatusProps,
  IPurchaseInvoiceImportStatusResponse
> = async ({ slug, jobId }) => {
  const url = `/v1/invoicing/${slug}/purchase_invoice/import/${jobId}/status/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'GET',
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}
